import React, { useState, useEffect, useId } from 'react';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ID, useAccordionContext } from './_AccordionContext';
import { navigationClicked } from '@repo/ui/src/services/analytics';

interface AccordionItemProps {
  children: React.ReactNode;
  heading: string;
  uuid?: ID;
  className?: string;
  headerClassName?: string;
  headerFontClass?: string;
  onClick?: (isOpen: boolean) => void;
}

export const AccordionItem = ({
  heading,
  uuid,
  children,
  className,
  headerClassName,
  headerFontClass = 'tra-text-web-heading-5',
}: AccordionItemProps) => {
  const _id = useId();
  const id = uuid ?? _id;
  const { openDrawer, previousOpenDrawer, toggleDrawer, dark, divider } = useAccordionContext();
  const isOpen = !!openDrawer?.includes(id);
  const previouslyOpen = !!previousOpenDrawer?.includes(id);
  const isOpening = isOpen && !previouslyOpen;
  const isClosing = !isOpen && previouslyOpen;
  const [isClosed, setIsClosed] = useState(!isOpen);
  const [finishedAnimation, setFinishedAnimation] = useState(true);

  useEffect(() => {
    if (isOpening) {
      const timer = setTimeout(() => {
        setIsClosed(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpening]);

  useEffect(() => setFinishedAnimation(false), [isOpening, isClosing]);

  const panelId = `accordion-panel-${id}`;
  const headingId = `accordion-heading-${id}`;

  return (
    <div className="tw-text-surface-800 tw-group" role="presentation">
      <button
        id={headingId}
        role="tab"
        aria-controls={panelId}
        aria-selected={isOpen ? 'true' : 'false'}
        aria-expanded={isOpen ? 'true' : 'false'}
        className={classNames(
          'tw-w-full tw-text-left tw-grid tw-grid-flow-col tw-justify-between tw-py-6 tw-cursor-pointer',
          'focus-visible:tw-outline-action-300 focus-visible:tw-outline-[3px] focus-visible:tw-outline',
          dark && 'tw-text-white',
          headerClassName,
          headerFontClass,
        )}
        onClick={() => {
          toggleDrawer(id);
          navigationClicked({
            nav_type: 'Top Navigation',
            clicked_text: heading,
            primary_header: heading,
          });
        }}
      >
        {heading}
        <span className="tw-w-6 tw-self-stretch" aria-hidden="true">
          <ChevronIcon direction={isOpen && !isClosed ? 'up' : 'down'} />
        </span>
      </button>
      <div
        className={classNames(
          'tw-grid tw-transition-all tw-duration-500 tw-ease-in-out',
          !isClosed && isOpen
            ? 'tw-grid-rows-[minmax(0,1fr)]'
            : 'tw-grid-rows-[minmax(0,0fr)]'
        )}
        onTransitionEnd={() => {
          if (isClosing && !isClosed) {
            setIsClosed(true);
          }
          setFinishedAnimation(true);
        }}
      >
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={headingId}
          tabIndex={0}
          className={classNames(
            'tw-my-6',
            divider && 'tw-mt-0',
            (isClosed || (!finishedAnimation && (isOpening || isClosing))) &&
              'tw-overflow-y-hidden',
            className,
          )}
        >
          {divider && (
            <hr className="tw-h-1 tw-w-12 tw-bg-action-400 tw-mb-5 tw-border-0" />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
